import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, Target, TrendingUp, Award, ArrowRight, Play, Youtube, CheckCircle } from 'lucide-react';
import axios from 'axios';
import YouTube from 'react-youtube';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CoachingPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/youtube/videos`);
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const opts = {
    height: '100%',
    width: '100%',
    playerVars: {
      autoplay: 0,
    },
  };

  const onPlayerError = (event) => {
    console.error('YouTube player error:', event);
  };

  const coachingServices = [
    {
      icon: <Users size={32} />,
      title: 'Executive Coaching',
      description: 'One-on-one coaching for CEOs and senior leaders to develop customer-first leadership skills.',
      features: [
        'Personalized coaching sessions',
        'Strategic planning support',
        'Leadership development',
        '360-degree feedback'
      ]
    },
    {
      icon: <Target size={32} />,
      title: 'Team Coaching',
      description: 'Group coaching sessions to align your leadership team around customer-first principles.',
      features: [
        'Team alignment workshops',
        'Culture transformation',
        'Communication improvement',
        'Conflict resolution'
      ]
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Organizational Transformation',
      description: 'Comprehensive programs to transform your entire organization into a customer-first business.',
      features: [
        'Full assessment and diagnosis',
        'Custom transformation roadmap',
        'Implementation support',
        'Ongoing measurement'
      ]
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Discovery',
      description: 'We start by understanding your unique challenges, goals, and organizational culture.'
    },
    {
      number: '02',
      title: 'Strategy',
      description: 'Together, we develop a customized roadmap for your customer-first transformation.'
    },
    {
      number: '03',
      title: 'Implementation',
      description: 'I work alongside you to implement strategies and overcome obstacles.'
    },
    {
      number: '04',
      title: 'Growth',
      description: 'We measure progress, celebrate wins, and continuously refine your approach.'
    }
  ];

  return (
    <div data-testid="coaching-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_growth-guide-21/artifacts/4edftbhr_Gemini_Generated_Image_2wzmx52wzmx52wzm.png"
                alt="The Servant CEO YouTube Channel"
                className="rounded-2xl shadow-2xl"
                data-testid="coaching-hero-image"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold" data-testid="coaching-hero-title">
                The Servant CEO YouTube Channel
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
                Business coaching videos from "The Customer First Business" author and Servant CEO, Will Owen
              </p>
              
              <div className="space-y-4 pt-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-accent flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Book-to-Video Curriculum:</h3>
                    <p className="text-muted-foreground">Master the "Serve, Solve, Scale" framework through tactical, step-by-step video lessons.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-accent flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Entrepreneurial Confidence:</h3>
                    <p className="text-muted-foreground">Bridge the gap between dreaming and doing with coaching designed to overcome uncertainty.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-accent flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Service-First Growth:</h3>
                    <p className="text-muted-foreground">Practical strategies to build a high-impact business that scales by putting the customer first.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Coaching Services */}
      <section className="section-padding bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" data-testid="services-title">
              Coaching Videos
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Concise and practical business coaching videos for entrepreneurs.
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
              <p className="mt-4 text-muted-foreground">Loading videos...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video, index) => (
                <motion.div
                  key={video.video_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  data-testid={`video-card-${index}`}
                  onClick={() => setSelectedVideo(video)}
                  className="glass-card rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-2 hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="relative aspect-video bg-secondary">
                    <img
                      src={video.thumbnail_url}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center">
                        <Play className="text-accent-foreground" size={32} />
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold mb-2 line-clamp-2">{video.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{video.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div 
            className="max-w-5xl w-full glass-card rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-video">
              <YouTube
                videoId={selectedVideo.video_id}
                opts={opts}
                onError={onPlayerError}
                className="w-full h-full"
                data-testid="video-player"
              />
            </div>
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-4">{selectedVideo.title}</h2>
              <p className="text-muted-foreground">{selectedVideo.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="section-padding bg-accent/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="cta-title">
              Subscribe for More Content
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Don't miss out on new business coaching videos. Subscribe to The Servant CEO YouTube channel today.
            </p>
            <a
              href="https://youtube.com/@TheServantCEO"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-subscribe-button"
              className="inline-flex items-center justify-center bg-[#FF0000] text-white hover:bg-[#CC0000] rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-105 shadow-lg"
            >
              <Youtube className="mr-2" size={24} />
              Subscribe on YouTube
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CoachingPage;